import { useRef, useCallback, useMemo, useState } from 'react';
import { TextInput } from '@interstate/components/TextInput';
import { IconButton } from '@interstate/components/IconButton';
import { EyeIcon, EyeSlashIcon } from '@interstate/components/Icons';
import { Spinner } from '@interstate/components/Spinner';
import MaskTextInput from './MaskTextInput';

interface RevealableMaskTextInputProps {
    name: string;
    label: string;
    value: string;
    maskedValue: string;
    onChange: (field: string, value: string) => void;
    disabled?: boolean;
    hasError?: boolean;
    errorMessage?: string;
    'data-testid'?: string;
    defaultMask?: boolean;
    /** When true, shows a loading spinner in the suffix instead of the toggle button. The component stays mounted so visibility state is preserved. */
    loading?: boolean;
    /** Called after each toggle — use for side effects like fetching SPD. Does not control visibility. */
    onToggle?: (isVisible: boolean) => void;
    /** When false, hides the reveal toggle entirely (no icon, no spinner) and the field stays masked. Defaults to true. Parent controls this — e.g. hide the toggle when a Can Desk feature toggle is on and the user lacks the permission. */
    canReveal?: boolean;
}

export const RevealableMaskTextInput = ({
    name,
    label,
    value,
    maskedValue,
    onChange,
    disabled,
    hasError,
    errorMessage,
    'data-testid': dataTestId,
    defaultMask,
    loading = false,
    onToggle,
    canReveal = true
}: RevealableMaskTextInputProps) => {
    const hadInitialValue = useRef(!!value);
    const [isVisible, setIsVisible] = useState(false);

    const toggleVisibility = useCallback(() => {
        const next = !isVisible;
        setIsVisible(next);
        onToggle?.(next);
    }, [isVisible, onToggle]);

    const hasNothingToReveal = !value && !defaultMask;

    const inputSuffix = useMemo(() => {
        if (!canReveal || hasNothingToReveal) return null;

        return (
            <div style={{ position: 'relative', left: 2 }} onMouseDown={(e: React.MouseEvent) => e.preventDefault()}>
                {loading ? (
                    <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24 }}>
                        <Spinner size={15} data-testid={dataTestId ? `${dataTestId}-loading` : undefined} />
                    </div>
                ) : (
                    <IconButton
                        size="sm"
                        buttonStyle="tertiary"
                        onClick={toggleVisibility}
                        data-testid={dataTestId ? `${dataTestId}-toggle` : undefined}
                        disabled={disabled}
                    >
                        {isVisible ? <EyeSlashIcon /> : <EyeIcon />}
                    </IconButton>
                )}
            </div>
        );
    }, [isVisible, loading, disabled, dataTestId, toggleVisibility, canReveal, hasNothingToReveal]);

    const commonProps = {
        name,
        label,
        size: 'small' as const,
        type: 'text' as const,
        'data-testid': dataTestId,
        value,
        disabled: disabled || loading,
        hasError,
        errorMessage,
        inputSuffix,
        sx: { '& svg': { color: 'inherit!important' } }
    };

    if (isVisible) {
        return <TextInput {...commonProps} onChange={(e) => onChange(name, e.target.value)} />;
    }

    return (
        <MaskTextInput
            {...commonProps}
            maskedValue={maskedValue}
            onChange={onChange}
            defaultMask={defaultMask ?? hadInitialValue.current}
        />
    );
};
